import { useState, useEffect, useRef } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { MessageSquare, CheckCircle2, PauseCircle, AlertTriangle, X, Trash2, RefreshCw, Plus, Pencil, Search } from "lucide-react";

/* ── Roboto font ─────────────────────────────────────────────────────────── */
const FONT_LINK =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap";

/* ── Design tokens (mirrors UserMaster's T object) ───────────────────────── */
const T = {
  violet:      "#5C2D91",
  violetMid:   "#7B3FB5",
  violetLight: "#EDE6F7",
  violetFaint: "#F7F3FD",
  red:         "#C8102E",
  redLight:    "#FCEAED",
  orange:      "#E8640A",
  orangeLight: "#FEF0E6",
  green:       "#1A7A42",
  greenLight:  "#E2F5EC",
  teal:        "#0D9488",
  tealLight:   "#F0FDFA",
  blue:        "#1D4ED8",
  blueLight:   "#EFF6FF",
  bg:          "#F4F0FA",
  surface:     "#FFFFFF",
  surfaceAlt:  "#FAF8FD",
  dark:        "#1A0A2E",
  muted:       "#6B5A8A",
  faint:       "#C4B8DC",
  border:      "rgba(92,45,145,0.12)",
  borderSolid: "#E8E0F5",
  shadow:      "0 2px 12px rgba(92,45,145,0.06)",
  shadowMd:    "0 4px 20px rgba(92,45,145,0.12)",
};

/* ── Label style ─────────────────────────────────────────────────────────── */
const LabelSty = {
  fontSize: 11, fontWeight: 700, color: T.muted,
  textTransform: "uppercase", letterSpacing: "0.07em",
  display: "block", marginBottom: 6,
};

/* ── Input style ─────────────────────────────────────────────────────────── */
const inputSty = (focused) => ({
  width: "100%", borderRadius: 10,
  border: `1.5px solid ${focused ? T.violet : T.borderSolid}`,
  padding: "10px 14px", fontSize: 14, color: T.dark,
  background: focused ? T.surface : T.surfaceAlt,
  outline: "none",fontFamily: "Montserrat, sans-serif",
  boxSizing: "border-box", transition: "all 0.18s",
  boxShadow: focused ? `0 0 0 3px ${T.violetLight}` : "none",
});

/* ── Topic avatar — initials circle ─────────────────────────────────────── */
function TopicAvatar({ title = "", index = 0 }) {
  const GRADS = [
    `linear-gradient(135deg,${T.violet},${T.violetMid})`,
    `linear-gradient(135deg,${T.teal},#14B8A6)`,
    `linear-gradient(135deg,${T.orange},#F5830A)`,
    `linear-gradient(135deg,${T.blue},#3B82F6)`,
    `linear-gradient(135deg,${T.red},#E02040)`,
    `linear-gradient(135deg,${T.green},#2EAA5E)`,
  ];
  const init = (title || "?")
    .split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div style={{
      width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
      background: GRADS[index % GRADS.length],
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 13, fontWeight: 800, color: "#fff",
      boxShadow: `0 2px 8px rgba(92,45,145,0.28)`,
      letterSpacing: "0.02em",
    }}>{init}</div>
  );
}
/* ── Action button (mirrors RoleMaster exactly) ──────────────────────────── */
function ActionBtn({ title, onClick, color, bg, border, children }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? color : bg,
        color:      hov ? "#fff" : color,
        border:     `1px solid ${hov ? color : border}`,
        padding: "7px 16px", borderRadius: 10,
        fontSize: 12, fontWeight: 700, cursor: "pointer",
        transition: "all 0.15s", whiteSpace: "nowrap",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >{children}</button>
  );
}

/* ── Modal component ─────────────────────────────────────────────────────── */
function Modal({ modal, onClose }) {
  if (!modal) return null;
  const isConfirm = modal.type === "confirm";
  const isError   = modal.type === "error";
const iconMap = {
  success:    <CheckCircle2 size={22} />,
  error:      <X size={22} />,
  confirm:    <Trash2 size={22} />,
  validation: <AlertTriangle size={22} />,
};
  const colorMap  = {
    success:    { accent: T.green,  light: T.greenLight, border: "#A7F3D0" },
    error:      { accent: T.red,    light: T.redLight,   border: "#FECDD3" },
    confirm:    { accent: T.red,    light: T.redLight,   border: "#FECDD3" },
    validation: { accent: T.orange, light: T.orangeLight,border: "#FED7AA" },
  };
  const c = colorMap[modal.type] || colorMap.success;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(26,10,46,0.45)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "dtmFadeIn 0.18s ease",
    }}>
      <style>{`
        @keyframes dtmFadeIn  { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }
      `}</style>
      <div style={{
        background: T.surface, borderRadius: 20, width: "100%", maxWidth: 420,
        boxShadow: "0 20px 60px rgba(92,45,145,0.22), 0 4px 16px rgba(0,0,0,0.10)",
        border: `1px solid ${T.borderSolid}`, overflow: "hidden",
      }}>
        {/* Accent top bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${c.accent})` }} />
        <div style={{ padding: "28px 28px 24px" }}>
          {/* Icon + title */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: c.light, border: `1.5px solid ${c.border}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>{iconMap[modal.type]}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: T.dark, lineHeight: 1.25 }}>{modal.title}</div>
              {modal.subtitle && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{modal.subtitle}</div>}
            </div>
          </div>
          {/* Message */}
          <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.65, margin: "0 0 24px", fontWeight: 400 }}>
            {modal.message}
          </p>
          {/* Buttons */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {isConfirm && (
              <button onClick={onClose} style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 600,
                background: T.surfaceAlt, color: T.muted,
                border: `1.5px solid ${T.borderSolid}`, cursor: "pointer",
              }}>Cancel</button>
            )}
            <button
              onClick={() => { if (modal.onConfirm) modal.onConfirm(); onClose(); }}
              style={{
                padding: "10px 22px", borderRadius: 11, fontSize: 13, fontWeight: 700,
                background: isConfirm || isError
                  ? `linear-gradient(135deg,${T.red},#E02040)`
                  : `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none", cursor: "pointer",
                boxShadow: `0 4px 14px ${isConfirm || isError ? T.red : T.violet}40`,
              }}
            >{isConfirm ? "Delete" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════ */
export default function DiscussionTopicMaster() {

  /* ── ALL STATE — UNCHANGED ── */
  const [topics,     setTopics]     = useState([]);
  const [formData,   setFormData]   = useState({ title: "", description: "", status: "Active" });
  const [editingId,  setEditingId]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [showForm,   setShowForm]   = useState(false);
  const [hov,        setHov]        = useState(null);
  const [foc,        setFoc]        = useState({});
  const [modal, setModal] = useState(null);
const showModal = (type, title, message, onConfirm = null, subtitle = "") =>
  setModal({ type, title, message, onConfirm, subtitle });
const closeModal = () => setModal(null);
  const fIn  = (k) => () => setFoc(p => ({ ...p, [k]: true  }));
  const fOut = (k) => () => setFoc(p => ({ ...p, [k]: false }));
const [page, setPage] = useState(1);
const PER_PAGE = 10;
const formRef = useRef(null);
const [refreshing, setRefreshing] = useState(false); // ← add this

  /* ── ALL LOGIC — UNCHANGED ── */
  const fetchTopics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res   = await API.get("/admin/discussion-topics", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setTopics(res.data.data || []);
    } catch (err) {
      console.error("Error loading Segment feeds:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTopics(); }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
  const topicTitle = formData.title.trim();
const topicDescription = formData.description.trim();

// Required
if (!topicTitle) {
  showModal(
    "validation",
    "Validation Error",
    "Topic title is required."
  );
  return;
}

// Only letters & spaces
if (!/^[A-Za-z ]+$/.test(topicTitle)) {
  showModal(
    "validation",
    "Validation Error",
    "Topic title can contain only letters and spaces."
  );
  return;
}

// Minimum Length
if (topicTitle.length < 3) {
  showModal(
    "validation",
    "Validation Error",
    "Topic title must be at least 3 characters."
  );
  return;
}

// Maximum Length
if (topicTitle.length > 50) {
  showModal(
    "validation",
    "Validation Error",
    "Topic title cannot exceed 50 characters."
  );
  return;
}

// Description Length
if (topicDescription.length > 250) {
  showModal(
    "validation",
    "Validation Error",
    "Description cannot exceed 250 characters."
  );
  return;
}


    try {
      const token  = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      let res;const payload = {
  ...formData,
  title: topicTitle,
  description: topicDescription
};
      if (editingId) {
       res = await API.put(
  `/admin/discussion-topics/${editingId}`,
  payload,
  config
);
      } else {
     res = await API.post(
  "/admin/discussion-topics",
  payload,
  config
);
      }
      if (res.data.success) {
       showModal("success",
  editingId ? "Topic Updated" : "Topic Created",
  editingId ? "Segment updated successfully." : "Segment created successfully."
);
        if (editingId) {
          setTopics(prev => prev.map(t => t.id === editingId ? { ...t, ...formData } : t));
        } else {
          await fetchTopics();
        }
        setFormData({ title: "", description: "", status: "Active" });
        setEditingId(null);
        setShowForm(false);
      }
    } catch (err) {
      console.error("Save error:", err);
      const msg = err.response?.data?.message || "Unable to save the segment. Please try again.";
      const isDuplicate = msg.toLowerCase().includes("already exists");
      showModal(
        isDuplicate ? "validation" : "error",
        isDuplicate ? "Duplicate Segment" : "Save Failed",
        msg
      );
    }
  };

  const handleEdit = (topic) => {
  setFormData({ title: topic.title || "", description: topic.description || "", status: topic.status || "Active" });
  setEditingId(topic.id);
  setShowForm(true);
  setTimeout(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 100);
};

  const handleDelete = async (id) => {
  showModal(
    "confirm",
    "Delete Segment",
    "Are you sure you want to delete this segment? This action cannot be undone.",
    async () => {
      try {
        const token = localStorage.getItem("token");
        await API.delete(`/admin/discussion-topics/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchTopics();
        showModal("success", "Segment Deleted", "Segment deleted successfully.");
      } catch (err) {
        showModal(
          "error",
          "Delete Failed",
          "Unable to delete the segment. Please try again later."
        );
      }
    }
  );
};
  const handleResetForm = () => {
    setFormData({ title: "", description: "", status: "Active" });
    setEditingId(null);
    setShowForm(false);
  };

  const filteredTopics = topics.filter(t =>
    (t.title       || "").toLowerCase().includes(search.toLowerCase()) ||
    (t.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const activeCount   = topics.filter(t => t.status === "Active").length;
  const inactiveCount = topics.filter(t => t.status === "Inactive").length;
  const totalPages = Math.max(1, Math.ceil(filteredTopics.length / PER_PAGE));
const paged      = filteredTopics.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <AdminLayout>
      <Modal modal={modal} onClose={closeModal} />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href={FONT_LINK} rel="stylesheet" />

      <style>{`
        .dtm-root *, .dtm-root *::before, .dtm-root *::after {
          box-sizing: border-box;
font-family: 'Montserrat', sans-serif !important;
        }
        .dtm-row:hover  { background: ${T.violetFaint} !important; }
        .dtm-pill       { transition: all 0.15s ease !important; }
        .dtm-pill:hover { transform: translateY(-1px); }
        .dtm-add-btn:hover { box-shadow: 0 6px 20px rgba(92,45,145,0.45) !important; transform: translateY(-1px); }
        .dtm-add-btn    { transition: all 0.18s ease !important; }
       .dtm-refresh:hover { opacity: 0.82; }
.dtm-refresh    { transition: opacity 0.15s; cursor: pointer; }
@keyframes dtm-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } /* ← add this */
        
        .dtm-search:focus-within {
          border-color: ${T.violet} !important;
          box-shadow: 0 0 0 3px rgba(92,45,145,0.10) !important;
        }
        @media (max-width: 900px) {
          .dtm-header  { flex-direction: column !important; gap: 14px !important; }
          .dtm-kpi     { grid-template-columns: 1fr 1fr !important; }
          .dtm-frow    { flex-direction: column !important; align-items: flex-start !important; }
          .dtm-hide-md { display: none !important; }
        }
        @media (max-width: 480px) {
          .dtm-kpi     { grid-template-columns: 1fr 1fr !important; }
          .dtm-pad     { padding: 14px 12px !important; }
          .dtm-fgrid   { grid-template-columns: 1fr !important; }
        }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: ${T.faint}; border-radius: 4px; }
      `}</style>

      <div className="dtm-root dtm-pad" style={{ padding: "28px 32px", background: T.bg, minHeight: "100vh" }}>

        {/* ── PAGE HEADER ── */}
        <div className="dtm-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${T.violet},${T.orange})` }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Admin · Content Management
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
              <h1 style={{ fontWeight: 800, fontSize: 28, color: T.dark, margin: 0, letterSpacing: "-0.5px" }}>
                Segment 
              </h1>
              <span style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", fontSize: 9, fontWeight: 700,
                padding: "3px 10px", borderRadius: 20, letterSpacing: "0.1em",
                textTransform: "uppercase", boxShadow: `0 2px 8px ${T.violet}40`,
              }}>Admin</span>
            </div>
            <p style={{ color: T.muted, fontSize: 13, margin: 0, fontWeight: 400 }}>
              Manage segment categories across all projects
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
  type="button"
  className="dtm-refresh"
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchTopics(), minSpin]);
    setRefreshing(false);
  }}
  title="Refresh"
  disabled={refreshing}
  style={{
    width: 40, height: 40, borderRadius: 11,
    border: `1.5px solid ${T.borderSolid}`,
    background: T.surface, color: T.muted,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, flexShrink: 0, cursor: refreshing ? "not-allowed" : "pointer",
    opacity: refreshing ? 0.7 : 1,
  }}
>
  <span style={{ display: "inline-flex", animation: refreshing ? "dtm-spin 0.7s linear infinite" : "none" }}>
  <RefreshCw size={16} />
</span>
</button>

            <button
              className="dtm-add-btn"
              onClick={() => { if (showForm) { handleResetForm(); } else { setShowForm(true); } }}
              style={{
                background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
                color: "#fff", border: "none",
                padding: "11px 22px", borderRadius: 12,
                fontSize: 13, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: `0 4px 14px ${T.violet}40`,
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
             <Plus size={16} />
{editingId ? "Edit Topic" : showForm ? "Close Form" : "Add Segment"}
            </button>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="dtm-kpi" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Segments",    value: loading ? "…" : topics.length,      icon: <MessageSquare size={22} />, color: T.violet, bg: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: "#D8C8F5" },
            { label: "Active Segments",   value: loading ? "…" : activeCount,        icon: <CheckCircle2 size={22} />, color: T.green,  bg: `linear-gradient(135deg,${T.greenLight},#D0F5E3)`,  border: "#A7F3D0" },
            { label: "Inactive Segments", value: loading ? "…" : inactiveCount,      icon: <PauseCircle size={22} />, color: T.red,    bg: `linear-gradient(135deg,${T.redLight},#FFE0E5)`,    border: "#FECDD3" },
          ].map((s) => (
            <div key={s.label} style={{
              background: T.surface, borderRadius: 16, border: `1px solid ${T.borderSolid}`,
              padding: "20px 22px", display: "flex", alignItems: "center", gap: 14,
              boxShadow: T.shadow, position: "relative", overflow: "hidden",
            }}>
              <div style={{ position: "absolute", right: -10, top: -10, width: 70, height: 70, borderRadius: "50%", background: s.bg, opacity: 0.6 }} />
              <div style={{ width: 46, height: 46, borderRadius: 13, background: s.bg, border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, position: "relative" }}>
                {s.icon}
              </div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                <div style={{ fontWeight: 800, fontSize: 28, color: s.color, lineHeight: 1 }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>

       {/* ── COLLAPSIBLE FORM ── */}
{showForm && (
  <div ref={formRef} style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", marginBottom: 24, boxShadow: T.shadowMd, scrollMarginTop: 100 }}>
            <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />
            <div style={{ padding: "22px 26px" }}>

              {/* Form header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
{editingId ? <Pencil size={16} /> : <Plus size={18} />}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>
                    {editingId ? "Edit Segment Title" : "Create New Segment"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted }}>Fill in the details below</div>
                </div>
              </div>

              {/* Row 1: title + status */}
              <div className="dtm-fgrid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 16 }}>
                {/* Topic title */}
                <div>
                  <label style={LabelSty}>Segment Title *</label>
                  <input
                    type="text" name="title" value={formData.title} onChange={handleChange}
                    placeholder="e.g. Health & Safety Compliance"
                    onFocus={fIn("title")} onBlur={fOut("title")}
                    style={inputSty(foc.title)}
                  />
                </div>

                {/* Status toggle */}
                <div>
                  <label style={LabelSty}>Status</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["Active", "Inactive"].map((s) => {
                      const isActive = formData.status === s;
                      const col = s === "Active" ? T.green : T.red;
                      const bg  = s === "Active" ? T.greenLight : T.redLight;
                      return (
                        <button
                          key={s} type="button"
                          onClick={() => setFormData({ ...formData, status: s })}
                          style={{
                            flex: 1, padding: "10px 0", borderRadius: 10,
                            border: `1.5px solid ${isActive ? col : T.borderSolid}`,
                            background: isActive ? bg : T.surfaceAlt,
                            color: isActive ? col : T.muted,
                            fontSize: 13, fontWeight: 700, cursor: "pointer",
                            fontFamily: "Montserrat, sans-serif",
                            transition: "all 0.15s",
                          }}
                        >
                          {s === "Active"
  ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><CheckCircle2 size={14} /> Active</span>
  : <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><PauseCircle size={14} /> Inactive</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 20 }}>
                <label style={LabelSty}>Description</label>
                <textarea
                  rows={3} name="description" value={formData.description} onChange={handleChange}
                  placeholder="Describe the segments under this category…"
                  onFocus={fIn("desc")} onBlur={fOut("desc")}
                  style={{ ...inputSty(foc.desc), resize: "vertical" }}
                />
              </div>

{/* Buttons */}
<div style={{ display: "flex", gap: 10 }}>
  <button
    onClick={handleSave}
    style={{
      background: `linear-gradient(135deg,${T.violet},${T.violetMid})`,
      color: "#fff", border: "none", padding: "11px 28px", borderRadius: 12,
      fontSize: 14, fontWeight: 700, cursor: "pointer",
      boxShadow: `0 4px 14px ${T.violet}40`, transition: "all 0.2s",
    }}
  >
    {editingId ? "Update Segment" : "Save Segment"}
  </button>
  <button
    onClick={handleResetForm}
    style={{
      background: T.surfaceAlt, color: T.muted,
      border: `1.5px solid ${T.borderSolid}`, padding: "11px 20px",
      borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer",
    }}
  >Cancel</button>
</div>
            </div>
          </div>
        )}

        {/* ── TABLE CARD ── */}
        <div style={{ background: T.surface, borderRadius: 20, border: `1px solid ${T.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(92,45,145,0.08)" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg,${T.violet},${T.red},${T.orange})` }} />

          <div style={{ padding: "20px 24px" }}>

            {/* Toolbar */}
            <div className="dtm-frow" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 14, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: T.dark }}>All Segments</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 3, fontWeight: 400 }}>
                  {filteredTopics.length} of {topics.length} categories
                  {search && <span style={{ color: T.violet, fontWeight: 600 }}> · filtered</span>}
                </div>
              </div>

              {/* Search */}
              <div className="dtm-search" style={{ display: "flex", alignItems: "center", gap: 8, background: T.surfaceAlt, border: `1.5px solid ${T.borderSolid}`, borderRadius: 12, padding: "9px 14px", minWidth: 260, transition: "all 0.2s" }}>
  <span style={{ color: T.faint, display: "inline-flex" }}><Search size={15} /></span>
  <input
                  type="text" placeholder="Search segment or description…"
                  value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  style={{ border: "none", background: "transparent", fontSize: 13, color: T.dark, outline: "none", width: "100%", fontWeight: 400 }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{ background: T.violetLight, border: "none", color: T.violet, cursor: "pointer", fontSize: 11, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}
                  >✕</button>
                )}
              </div>
            </div>

            {/* Empty / loading states */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
<div style={{ width: 56, height: 56, borderRadius: 16, background: T.violetLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <MessageSquare size={26} color={T.violet} />
</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: T.dark }}>Loading topics…</div>
                <div style={{ fontSize: 12, color: T.muted }}>Connecting to Segment registry</div>
              </div>
            ) : filteredTopics.length === 0 ? (
              <div style={{ textAlign: "center", padding: "64px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
<div style={{ width: 64, height: 64, borderRadius: 18, background: `linear-gradient(135deg,${T.violetLight},#F0E8FF)`, border: `2px dashed ${T.faint}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <MessageSquare size={28} color={T.violet} />
</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: T.dark, marginBottom: 6 }}>
                    {search ? "No matching segments" : "No segments yet"}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, maxWidth: 300, lineHeight: 1.6 }}>
                    {search
                      ? `No results for "${search}". Try a different search.`
                      : "Click '+ Add Segment' to create the first Segment category."}
                  </div>
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      style={{ marginTop: 14, background: `linear-gradient(135deg,${T.violet},${T.violetMid})`, color: "#fff", border: "none", padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >Clear Search</button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 620 }}>
                  <thead>
                    <tr style={{ background: T.violetFaint }}>
                      {["#", "Title", "Description", "Status", "Actions"].map((h, i) => (
                        <th key={h} style={{
                          fontSize: 10, fontWeight: 700, color: T.muted,
                          textTransform: "uppercase", letterSpacing: "0.09em",
                          padding: "12px 16px", textAlign: i === 0 ? "center" : "left",
                          borderBottom: `2px solid ${T.borderSolid}`,
                          borderTop:    `1px solid ${T.borderSolid}`,
                          whiteSpace: "nowrap", background: T.violetFaint,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                   {paged.map((topic, i) => {
                     const isInactive = topic.status === "Inactive";
                      const isLast     = i === paged.length - 1;
                      return (
                        <tr
                          key={topic.id}
                          className="dtm-row"
                          onMouseEnter={() => setHov(topic.id)}
                          onMouseLeave={() => setHov(null)}
                          style={{ background: T.surface, transition: "background 0.12s", cursor: "default" }}
                        >
                          {/* # */}
<td style={{ padding: "15px 16px", textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, background: T.violetFaint, fontWeight: 700, fontSize: 11, color: T.muted }}>
    {(page - 1) * PER_PAGE + i + 1}
  </span>
</td>

                          {/* Topic — title only */}
<td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <div style={{ fontSize: 14, fontWeight: 700, color: T.dark }}>{topic.title}</div>
</td>

                          {/* Description */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}`, maxWidth: 320 }}>
                            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.5 }}>
                              {topic.description
                                ? topic.description.length > 80
                                  ? topic.description.slice(0, 80) + "…"
                                  : topic.description
                                : <span style={{ color: T.faint }}>—</span>}
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
                            <span style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 20,
                              background: isInactive ? T.redLight   : T.greenLight,
                              color:      isInactive ? T.red        : T.green,
                              border:     `1px solid ${isInactive ? "#FECDD3" : "#A7F3D0"}`,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />
                              {topic.status}
                            </span>
                          </td>

                          
{/* Actions */}
{/* Actions */}
<td style={{ padding: "15px 16px", borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>
  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
    <ActionBtn title="Edit Topic"   onClick={() => handleEdit(topic)}      color={T.violet} bg={T.violetLight} border={T.borderSolid}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
    </ActionBtn>
    <ActionBtn title="Delete Topic" onClick={() => handleDelete(topic.id)} color={T.red}    bg={T.redLight}    border="#F8C8D0">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
    </ActionBtn>
  </div>
</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
          
{!loading && filteredTopics.length > 0 && (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderSolid}`, flexWrap: "wrap", gap: 12 }}>
    <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>
      Showing <strong style={{ color: T.dark }}>{Math.min((page - 1) * PER_PAGE + 1, filteredTopics.length)}–{Math.min(page * PER_PAGE, filteredTopics.length)}</strong> of <strong style={{ color: T.dark }}>{filteredTopics.length}</strong> categories
    </span>
    {totalPages > 1 && (
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
          style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === 1 ? T.borderSolid : T.violet}`, background: page === 1 ? T.surface : T.violetLight, color: page === 1 ? T.faint : T.violet, cursor: page === 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, transition: "all 0.15s" }}>‹ Prev</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))
          .map(n => (
            <button key={n} onClick={() => setPage(n)}
              style={{ width: 34, height: 34, borderRadius: 10, border: `1.5px solid ${n === page ? T.violet : T.borderSolid}`, background: n === page ? `linear-gradient(135deg,${T.violet},${T.violetMid})` : T.surface, color: n === page ? "#fff" : T.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: n === page ? `0 2px 8px ${T.violet}40` : "none", transition: "all 0.15s" }}>{n}</button>
          ))}
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
          style={{ height: 34, padding: "0 14px", borderRadius: 10, border: `1.5px solid ${page === totalPages ? T.borderSolid : T.violet}`, background: page === totalPages ? T.surface : T.violetLight, color: page === totalPages ? T.faint : T.violet, cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, transition: "all 0.15s" }}>Next ›</button>
      </div>
    )}
  </div>
)}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}